'use client';

import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

type CuteSpinnerProperties = {
  /**
   * Width/height for the spinner. Accepts numbers (pixels) or any CSS length.
   */
  size?: number | string;
  'aria-label'?: string;
} & HTMLAttributes<HTMLSpanElement>;

export function CuteSpinner({
  size = 20,
  className,
  'aria-label': ariaLabel,
  role,
  'aria-live': ariaLive,
  'aria-hidden': ariaHidden,
  ...rest
}: CuteSpinnerProperties) {
  const dimension = typeof size === 'number' ? `${String(size)}px` : size;
  const normalizedClassName = className?.replaceAll(/\banimate-spin\b/g, '') ?? undefined;

  const hasExplicitAccessibility = role !== undefined || ariaLive !== undefined || ariaHidden !== undefined;

  let accessibilityProperties: Record<string, unknown>;

  if (ariaLabel) {
    accessibilityProperties = {
      role: role ?? ('status' as const),
      'aria-live': ariaLive ?? ('polite' as const),
      'aria-label': ariaLabel,
    };
  } else if (hasExplicitAccessibility) {
    accessibilityProperties = {
      role,
      'aria-live': ariaLive,
      'aria-hidden': ariaHidden,
    };
  } else {
    accessibilityProperties = { 'aria-hidden': true as const };
  }

  return (
    <span
      {...accessibilityProperties}
      {...rest}
      className={cn(
        'inline-flex shrink-0 animate-spin rounded-full border-[3px] border-primary/20 border-t-primary bg-transparent',
        'shadow-[0001px_rgba(109,40,217,0.08)]',
        normalizedClassName
      )}
      style={{ width: dimension, height: dimension }}
    />
  );
}
